import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import BusinessSettings from './BusinessSettings';
import Users from './Users';
import FiscalConfig from './FiscalConfig';
import PrintSettings from '../components/PrintSettings';
import SubscriptionSettings from '../components/SubscriptionSettings';
import { hasPerm, PERM_KEYS } from '../utils/permissions';

// Página de Configuración centralizada con tabs para: Empresa, Impresión, Usuarios y Fiscal.
// Cada tab muestra componente embebido (cuando es seguro) o botones de acceso.
// Impresión reutiliza el modal existente PrintSettings, aquí como overlay.

const tabOrder = [
  { key: 'empresa', label: '🏢 Empresa', perm: PERM_KEYS.SETTINGS_BUSINESS },
  { key: 'suscripcion', label: '💳 Suscripción', perm: null }, // Visible para todos
  // { key: 'impresion', label: '🖨️ Impresión', perm: PERM_KEYS.SETTINGS_PRINT }, // Moved to Empresa
  { key: 'usuarios', label: '👥 Usuarios', perm: PERM_KEYS.USERS_MANAGE },
  { key: 'fiscal', label: '🧾 Fiscal', perm: PERM_KEYS.FISCAL_ACCESS }
];

export default function SettingsPage() {
  const { user } = useSelector(state => state.auth);
  const navigate = useNavigate();
  const [active, setActive] = useState(() => {
    const first = tabOrder.find(t => hasPerm(user, t.perm));
    return first ? first.key : 'empresa';
  });
  const [printOpen, setPrintOpen] = useState(false);

  const allowedTabs = tabOrder.filter(t => t.perm === null || hasPerm(user, t.perm));

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: 'system-ui, Arial, sans-serif' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => navigate(-1)} style={backBtn}>← Volver</button>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800 }}>⚙️ Configuración</h1>
        </div>
        <div style={{ fontSize: 12, color: '#555' }}>Centraliza ajustes clave</div>
      </div>

      <div style={tabsBarStyle}>
        {allowedTabs.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            style={{ ...tabBtnStyle, ...(active === t.key ? activeTabStyle : {}) }}
          >{t.label}</button>
        ))}
        {!allowedTabs.length && <div style={{ padding: 12, fontSize: 13 }}>No tienes permisos para ver configuraciones.</div>}
      </div>

      <div style={panelStyle}>
        {active === 'empresa' && hasPerm(user, PERM_KEYS.SETTINGS_BUSINESS) && (
          <div>
            <h2 style={panelTitle}>Empresa</h2>
            <BusinessSettings />
          </div>
        )}
        {active === 'impresion' && hasPerm(user, PERM_KEYS.SETTINGS_PRINT) && (
          <div>
            <h2 style={panelTitle}>Impresión</h2>
            <p style={hintStyle}>Ajusta opciones de impresión de tickets y comprobantes.</p>
            <button onClick={() => setPrintOpen(true)} style={primaryBtn}>Abrir Configuración de Impresión</button>
            <PrintSettings open={printOpen} onClose={() => setPrintOpen(false)} />
          </div>
        )}
        {active === 'usuarios' && hasPerm(user, PERM_KEYS.USERS_MANAGE) && (
          <div>
            <h2 style={panelTitle}>Usuarios</h2>
            <Users embedded />
          </div>
        )}
        {active === 'suscripcion' && (
          <div>
            <h2 style={panelTitle}>Suscripción</h2>
            <SubscriptionSettings />
          </div>
        )}
        {active === 'fiscal' && hasPerm(user, PERM_KEYS.FISCAL_ACCESS) && (
          <div>
            <h2 style={panelTitle}>Fiscal</h2>
            <FiscalConfig embedded />
          </div>
        )}
      </div>
    </div>
  );
}

const backBtn = {
  background: '#343a40', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13, boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
};
const tabsBarStyle = {
  display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20
};
const tabBtnStyle = {
  padding: '10px 16px', borderRadius: 10, border: '1px solid #dee2e6', background: '#f8f9fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, letterSpacing: '.3px'
};
const activeTabStyle = {
  background: '#343a40', color: '#fff', border: '1px solid #343a40'
};
const panelStyle = {
  background: '#fff', border: '1px solid #dee2e6', borderRadius: 14, padding: 20, boxShadow: '0 4px 14px rgba(0,0,0,0.06)'
};
const panelTitle = { marginTop: 0, fontSize: 18, fontWeight: 700 };
const hintStyle = { fontSize: 13, color: '#555', margin: '4px 0 12px' };
const primaryBtn = { background: '#0d6efd', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 };
